/**
 * «Cambió el plano» (OPUS ci, idea 4), DWG/DXF: arma la comparación de dos revisiones de un dibujo con la hoja del
 * plano. Sin IA, $0, puro (lo que mide viene de afuera: el inventario de cada revisión, que el servidor ya guarda).
 *
 *  - Piezas: una por capa y planta que suma, y una por handle de cada bloque (`piezasDwg`, en @supernova/shared).
 *  - Medir un elemento (`medirA`/`medirB`): la MISMA foto que la pantalla del plano (`fotoDeElemento`), con el contexto
 *    de CADA revisión: su inventario, la escala común, las alturas de sus plantas y los altos de vanos de la hoja.
 *  - Antes de comparar se FRENA si las dos revisiones no se pueden medir con la misma unidad.
 *
 * Dos guardas que el diseño pide para no dar una cantidad mal sin avisar (devuelven null = «revisar»):
 *  - una selección con planta cuya capa ya no tiene medidas en esa planta (la pantalla mediría la capa completa);
 *  - el renglón se midió en A con altos propios por planta y por tipo de vano, y en la revisión falta alguno
 *    (`cuentaDeVanosDe` pasaría TODAS las plantas al alto general en silencio).
 *
 * La regla de qué cambia y qué se aplica solo vive en @supernova/shared (`compararRevisiones`).
 */
import {
  alturasDeB,
  cambiosDeVistas,
  capaSeMideComoMuro,
  compararRevisiones,
  emparejarVistas,
  escalaComparable,
  piezasDwg,
  vistasComparables,
  type AlturaDeVista,
  type CambioDeVista,
  type CapasClasificadasDTO,
  type ComoSeMidio,
  type ComparacionDePlanos,
  type DecisionDeCapa,
  type FotoGenerador,
  type MagnitudesPrincipal,
  type ParametrosCambioDePlano,
  type PlanoAlturaPropuesta,
  type PlanoEscalaElegida,
  type PlanoInventario,
  type PlanoRenglon,
  type PlanoSeleccion,
} from '@supernova/shared';
import { alturaPorVistaDwg } from './contextoDwg.js';
import { renglonParaImpacto } from './compararRevisionIfc.js';
import {
  asignablesDelPlano,
  cuentaDeVanosDe,
  fotoDeElemento,
  largoConVanosDe,
  magnitudesDelPrincipal,
  type Asignable,
  type ContextoAsignables,
  type ContextoFoto,
} from './medirElemento.js';
import { etiquetasDeNiveles } from './nivelesIfc.js';

type UnElemento = Omit<PlanoSeleccion, 'ademas'>;

/** Medir una revisión: la foto y las magnitudes de UN elemento, con los ajustes con que se midió el renglón. */
export interface MedidorDeRevisionDwg {
  medir: (sel: UnElemento, como: ComoSeMidio) => FotoGenerador | null;
  magnitudes: (sel: UnElemento, como: ComoSeMidio) => MagnitudesPrincipal | null;
  /** ¿El elemento tiene en esta revisión altos propios por planta y un alto por cada tipo de vano? (para la guarda de B). */
  completo: (sel: UnElemento, como: ComoSeMidio) => boolean;
}

export interface ContextoDeRevisionDwg {
  inventario: PlanoInventario;
  /** El factor de escala COMÚN a las dos revisiones (`escalaComparable`). */
  factorEscala: number;
  capasClasificadas: CapasClasificadasDTO | null;
  decisionOverride: Record<string, DecisionDeCapa>;
  alturaPorVista: Map<string, AlturaDeVista>;
  /** Los altos de los tipos de vano guardados en la hoja (`GET altos-vanos`); la revisión los hereda. */
  altosVanos: Readonly<Record<string, string>>;
  /** Solo la revisión nueva: si el renglón estaba completo en A, aquí debe estarlo también (guarda de alturas). */
  exigirCompletoComoEnA?: (sel: UnElemento, como: ComoSeMidio) => boolean;
}

const alto = (s: string | undefined) => {
  const n = Number((s ?? '').replace(',', '.'));
  return Number.isFinite(n) && n > 0;
};

/** El medidor de UNA revisión de un DWG: los contextos de `asignablesDelPlano` y `fotoDeElemento` con su inventario. */
export function medidorDeRevisionDwg(c: ContextoDeRevisionDwg): MedidorDeRevisionDwg {
  const ctxAsignables: ContextoAsignables = {
    inventario: c.inventario,
    factorEscala: c.factorEscala,
    capasClasificadas: c.capasClasificadas,
    decisionOverride: c.decisionOverride,
    alturaPorVista: c.alturaPorVista,
    escalaSinResolver: false,
  };
  const ctxFoto: ContextoFoto = {
    inventario: c.inventario,
    factorEscala: c.factorEscala,
    capasClasificadas: c.capasClasificadas,
    decisionOverride: c.decisionOverride,
    nivelDeElemento: new Map(),
    etiquetaNivel: etiquetasDeNiveles([]),
    piezaDelModelo: new Map(),
  };
  const capaPorNombre = new Map(c.inventario.capas.map((x) => [x.capa, x] as const));

  /** El elemento como lo arma la pantalla; null si en esta revisión no hay con qué medirlo. */
  const asignable = (sel: UnElemento): Asignable | null => {
    if (sel.tipo === 'bloque') {
      const b = c.inventario.bloques.find((x) => x.nombre === sel.clave);
      if (!b) return null;
      const ids = new Set(sel.ids ?? b.ids);
      return asignablesDelPlano(ctxAsignables, new Set(), ids, new Set(), null)[0] ?? null;
    }
    const capa = capaPorNombre.get(sel.clave);
    if (!capa) return null;
    // Guarda: con planta, la capa debe tener medidas EN esa planta; si no, la pantalla mediría la capa completa.
    // Excepción: un dibujo con UNA sola planta que suma no trae `por_vista`, y la capa completa ES esa planta.
    let vista = sel.vista ?? null;
    if (vista && !(capa.por_vista ?? []).some((pv) => pv.vista === vista)) {
      const sumando = (c.inventario.vistas ?? []).filter((v) => v.suma);
      if ((capa.por_vista ?? []).length === 0 && sumando.length === 1 && sumando[0]!.id === vista) vista = null;
      else return null;
    }
    return asignablesDelPlano(ctxAsignables, new Set([sel.clave]), new Set(), new Set(), vista ? { capa: sel.clave, vista } : null)[0] ?? null;
  };

  const altosDe = (como: ComoSeMidio): Record<string, string> => ({ ...c.altosVanos, ...como.altosVanos });

  /** Altos propios por planta y un alto por tipo de vano (lo que `cuentaDeVanosDe` necesita para no caer al alto general). */
  const completoEn = (a: Asignable, como: ComoSeMidio): boolean => {
    const mv = a.muroVanos;
    if (!mv) return true;
    if (!mv.tramos.every((t) => t.altoPropioM != null)) return false;
    const altos = altosDe(como);
    return mv.tipos.every((t) => alto(altos[t.clave]));
  };

  return {
    medir: (sel, como) => {
      const a = asignable(sel);
      if (!a) return null;
      if (c.exigirCompletoComoEnA?.(sel, como) && !completoEn(a, como)) return null;
      return fotoDeElemento(ctxFoto, a, {
        magnitud: como.magnitud,
        unidadConcepto: como.unidad,
        altoStr: como.altoStr,
        espesorStr: como.espesorStr,
        altosVanos: altosDe(como),
      });
    },
    magnitudes: (sel, como) => {
      const a = asignable(sel);
      if (!a) return null;
      if (c.exigirCompletoComoEnA?.(sel, como) && !completoEn(a, como)) return null;
      return magnitudesDelPrincipal(a, {
        altoStr: como.altoStr,
        espesorStr: como.espesorStr,
        cuentaVanos: cuentaDeVanosDe(a.muroVanos, como.altoStr, altosDe(como)),
        largoConVanosM: largoConVanosDe(a.muroVanos),
        capas: c.inventario.capas,
      });
    },
    completo: (sel, como) => {
      const a = asignable(sel);
      return a ? completoEn(a, como) : false;
    },
  };
}

/** Lo que se lee de CADA plano (el de la hoja y la revisión nueva). */
export interface DatosDePlanoDwg {
  inventario: PlanoInventario | null;
  capasClasificadas: CapasClasificadasDTO | null;
  /** Las correcciones de la persona (`decisionesCapa` de `getPlano`); la revisión hereda las del plano de la hoja. */
  decisionOverride: Record<string, DecisionDeCapa>;
  /** La unidad que la persona eligió para el ARCHIVO (`escala_dibujo_elegida`). */
  elegida: PlanoEscalaElegida | null;
}

export interface EntradaCompararDwg {
  a: DatosDePlanoDwg & { alturaProp: PlanoAlturaPropuesta | null };
  b: DatosDePlanoDwg;
  /** Los altos de los tipos de vano de la hoja (`GET altos-vanos`). */
  altosVanos: Readonly<Record<string, string>>;
  renglones: readonly PlanoRenglon[];
  parametros: ParametrosCambioDePlano;
  /** Fecha de la revisión nueva, ya escrita («13 de octubre de 2026»). */
  fechaRevision: string;
}

export type ResultadoCompararDwg =
  | { frenada: string }
  | { comparacion: ComparacionDePlanos; avisos: string[]; cambiosDeVistas: CambioDeVista[] };

/** Qué cambió en las plantas (agregada, quitada, dudosa, otra altura): para la pantalla, sin repetir la comparación. */
export function cambiosDeVistasDeRevision(e: Pick<EntradaCompararDwg, 'a' | 'b' | 'parametros'>): CambioDeVista[] {
  const invA = e.a.inventario;
  const invB = e.b.inventario;
  if (!invA || !invB) return [];
  const vistas = emparejarVistas(vistasComparables(invA), vistasComparables(invB), e.parametros);
  const alturasA = alturaPorVistaDwg(invA, e.a.alturaProp);
  return cambiosDeVistas(invA, invB, vistas, alturasA, alturasDeB(alturasA, invB, vistas));
}

/**
 * Compara dos revisiones de un DWG con la hoja del plano (vista previa; el servidor la valida al aplicar).
 * `frenada` = no se pudo comparar y se dice por qué (otra unidad, escala que no se sabe, plano sin leer).
 */
export function compararRevisionDwg(e: EntradaCompararDwg): ResultadoCompararDwg {
  const invA = e.a.inventario;
  const invB = e.b.inventario;
  if (!invA) return { frenada: 'El plano de tu hoja todavía no está leído: espera a que termine y vuelve a comparar.' };
  if (!invB) return { frenada: 'La revisión nueva todavía no está leída: espera a que termine y vuelve a comparar.' };

  const esc = escalaComparable(invA, e.a.elegida, invB, e.b.elegida);
  if (!esc.ok) return { frenada: esc.motivo };

  const vistas = emparejarVistas(vistasComparables(invA), vistasComparables(invB), e.parametros);
  const alturasA = alturaPorVistaDwg(invA, e.a.alturaProp);
  const alturasB = alturasDeB(alturasA, invB, vistas);
  const esMuroDe = (d: DatosDePlanoDwg) => (capa: string) => capaSeMideComoMuro(capa, d.capasClasificadas, d.decisionOverride[capa]);

  const medA = medidorDeRevisionDwg({
    inventario: invA,
    factorEscala: esc.factor,
    capasClasificadas: e.a.capasClasificadas,
    decisionOverride: e.a.decisionOverride,
    alturaPorVista: alturasA,
    altosVanos: e.altosVanos,
  });
  const medB = medidorDeRevisionDwg({
    inventario: invB,
    factorEscala: esc.factor,
    capasClasificadas: e.b.capasClasificadas,
    decisionOverride: e.b.decisionOverride,
    alturaPorVista: alturasB,
    altosVanos: e.altosVanos,
    // Si el renglón estaba completo en A (altos propios y altos de vano), en la revisión también debe estarlo.
    exigirCompletoComoEnA: (sel, como) => medA.completo(sel, como),
  });

  const comparacion = compararRevisiones({
    formato: 'dwg',
    parametros: e.parametros,
    piezasA: piezasDwg(invA, esc.factor, { lado: 'a', esMuro: esMuroDe(e.a) }),
    piezasB: piezasDwg(invB, esc.factor, { lado: 'b', vistas, esMuro: esMuroDe(e.b) }),
    vistasA: vistasComparables(invA),
    vistasB: vistasComparables(invB),
    renglones: e.renglones.map(renglonParaImpacto),
    medirA: medA.medir,
    medirB: medB.medir,
    magnitudesA: medA.magnitudes,
    magnitudesB: medB.magnitudes,
    fechaRevision: e.fechaRevision,
  });
  return {
    comparacion,
    avisos: esc.aviso ? [esc.aviso] : [],
    cambiosDeVistas: cambiosDeVistas(invA, invB, vistas, alturasA, alturasB),
  };
}
